// BLOKAS 1 CoinsSection.tsx: IMPORTAI
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Coins, Info, AlertCircle, ChevronRight, RefreshCw, Search, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useBinancePairs } from '../../hooks/useBinancePairs';
import { useMarketPrices } from '../../hooks/useMarketPrices';
import { Tooltip } from '../../ui/Tooltip';
import { debounce } from 'lodash-es';
import { useAuth } from '../../contexts/AuthContext';
console.log(`📦 [BLOKAS 1 CoinsSection.tsx] Importai užkrauti`);

// BLOKAS 2 CoinsSection.tsx: INTERFACE CoinsSectionProps
interface CoinsSectionProps {
  botId?: string;
  quoteCurrency?: string;
  onQuoteCurrencyChange?: (currency: string) => void;
  selectedCoins?: string[];
  onSelectedCoinsChange?: (coins: string[]) => void;
}
console.log(`📦 [BLOKAS 2 CoinsSection.tsx] Interface CoinsSectionProps apibrėžtas`);

// BLOKAS 3 CoinsSection.tsx: KOMPONENTAS CoinsSection
export const CoinsSection: React.FC<CoinsSectionProps> = ({
  botId,
  quoteCurrency = 'USDT',
  onQuoteCurrencyChange = () => {},
  selectedCoins = [],
  onSelectedCoinsChange = () => {}
}) => {
  console.log(`🔧 [BLOKAS 3 CoinsSection.tsx] Komponentas CoinsSection užkrautas`, { botId, quoteCurrency, selectedCoins });

  // SUB-BLOKAS 3.1 CoinsSection.tsx: STATE KINTAMIEJI
  const [searchTerm, setSearchTerm] = useState('');
  const { coins, quoteCurrencies, loading: pairsLoading, error: pairsError, refetch } = useBinancePairs(quoteCurrency);
  const { prices, loading: pricesLoading } = useMarketPrices(selectedCoins, quoteCurrency);
  const { user } = useAuth();
  console.log(`🔧 [SUB-BLOKAS 3.1 CoinsSection.tsx] State kintamieji inicijuoti`, { searchTerm, user: user?.id });

  // SUB-BLOKAS 3.2 CoinsSection.tsx: useMemo PERSIST COINS
  const persistCoins = useMemo(() =>
    debounce(async (botId: string, symbols: string[]) => {
      console.log(`🔧 [SUB-BLOKAS 3.2.1 CoinsSection.tsx] persistCoins funkcija iškviesta`, { botId, symbols });
      if (!botId || !user?.id) return;
      if (window.sendChange) {
        window.sendChange('selectedCoins', symbols);
      }
    }, 500),
    [user?.id]
  );

  // SUB-BLOKAS 3.3 CoinsSection.tsx: useMemo PERSIST QUOTE
  const persistQuote = useMemo(() =>
    debounce(async (botId: string, quote: string) => {
      console.log(`🔧 [SUB-BLOKAS 3.3.1 CoinsSection.tsx] persistQuote funkcija iškviesta`, { botId, quote });
      if (!botId || !user?.id) return;
      if (window.sendChange) {
        window.sendChange('quoteCurrency', quote);
      }
    }, 500),
    [user?.id]
  );

  // SUB-BLOKAS 3.4 CoinsSection.tsx: useEffect CLEANUP
  useEffect(() => () => {
    console.log(`🔧 [SUB-BLOKAS 3.4.1 CoinsSection.tsx] useEffect cleanup vykdomas`);
    persistCoins.cancel();
    persistQuote.cancel();
  }, [persistCoins, persistQuote]);

  // SUB-BLOKAS 3.5 CoinsSection.tsx: useEffect PERSIST COINS
  useEffect(() => {
    console.log(`🔧 [SUB-BLOKAS 3.5.1 CoinsSection.tsx] useEffect persistCoins vykdomas`, { botId, selectedCoins });
    if (!botId) return;
    persistCoins(botId, selectedCoins || []);
  }, [botId, selectedCoins, persistCoins]);

  // SUB-BLOKAS 3.6 CoinsSection.tsx: useEffect PERSIST QUOTE
  useEffect(() => {
    console.log(`🔧 [SUB-BLOKAS 3.6.1 CoinsSection.tsx] useEffect persistQuote vykdomas`, { botId, quoteCurrency });
    if (!botId) return;
    if (!quoteCurrency) return;
    persistQuote(botId, quoteCurrency);
  }, [botId, quoteCurrency, persistQuote]);

  // SUB-BLOKAS 3.7 CoinsSection.tsx: FILTRUOTOS COINS
  const filteredCoins = coins.filter(coin =>
    coin.toLowerCase().includes(searchTerm.toLowerCase())
  );
  console.log(`🔧 [SUB-BLOKAS 3.7 CoinsSection.tsx] filteredCoins sugeneruotos`, { 
    totalCoins: coins.length, 
    filteredCount: filteredCoins.length,
    searchTerm 
  });

  // SUB-BLOKAS 3.8 CoinsSection.tsx: FUNKCIJA handleToggleCoin
  const handleToggleCoin = (coin: string) => {
    console.log(`🔧 [SUB-BLOKAS 3.8.1 CoinsSection.tsx] handleToggleCoin iškviesta`, { coin });
    const src = Array.isArray(selectedCoins) ? selectedCoins : [];
    const has = src.includes(coin);
    const next = has ? src.filter(c => c !== coin) : [...src, coin];
    onSelectedCoinsChange(next);
  };

  // SUB-BLOKAS 3.9 CoinsSection.tsx: FUNKCIJA handleQuoteCurrencyChange
  const handleQuoteCurrencyChange = (currency: string) => {
    console.log(`🔧 [SUB-BLOKAS 3.9.1 CoinsSection.tsx] handleQuoteCurrencyChange iškviesta`, { currency });
    onQuoteCurrencyChange(currency);
    onSelectedCoinsChange([]);
    setSearchTerm('');
  };

  // SUB-BLOKAS 3.10 CoinsSection.tsx: FUNKCIJA formatPrice
  const formatPrice = (price: number) => {
    console.log(`🔧 [SUB-BLOKAS 3.10.1 CoinsSection.tsx] formatPrice iškviesta`, { price });
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(price);
  };

  // SUB-BLOKAS 3.11 CoinsSection.tsx: JSX GRAŽINIMAS
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        {/* SUB-BLOKAS 3.11.1 CoinsSection.tsx: HEADER */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Coins</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Select trading pairs for your bot</p>
          </div>
          <div className="h-12 w-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg flex items-center justify-center">
            <Coins className="h-6 w-6 text-yellow-500" />
          </div>
        </div>

        <div className="space-y-6">
          {/* SUB-BLOKAS 3.11.2 CoinsSection.tsx: QUOTE CURRENCY SELECTION */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Quote Currency
              </label>
              <Tooltip
                content={
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-medium text-blue-400 mb-2">Quote Currency</h5>
                      <div className="space-y-3">
                        <div>
                          <p className="font-medium text-blue-400 mb-1">What is it?</p>
                          <ul className="space-y-1 text-gray-300">
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              The currency you'll use to buy cryptocurrencies
                            </li>
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              Your account balance and profits are measured in this currency
                            </li>
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              All trading pairs will be [COIN]/[QUOTE] format
                            </li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium text-blue-400 mb-1">Popular Options</p>
                          <ul className="space-y-1 text-gray-300">
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              USDT: Most stable, widely supported
                            </li>
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              BTC: For Bitcoin-based trading
                            </li>
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              ETH: For Ethereum-based trading
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
                      <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-blue-300 text-xs">
                        Choose the currency you have in your exchange account. USDT is recommended for beginners.
                      </p>
                    </div>
                  </div>
                }
                position="right"
              >
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {quoteCurrencies.map((currency) => (
                <button
                  key={currency}
                  onClick={() => {
                    console.log(`🔧 [SUB-BLOKAS 3.11.2.1 CoinsSection.tsx] Quote currency pasirinkta`, { currency });
                    handleQuoteCurrencyChange(currency);
                  }}
                  className={cn(
                    "p-3 rounded-lg border text-center transition-all",
                    quoteCurrency === currency
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 text-gray-700 dark:text-gray-300"
                  )}
                >
                  <span className="font-medium">{currency}</span>
                </button>
              ))}
            </div>
          </div>

          {/* SUB-BLOKAS 3.11.3 CoinsSection.tsx: COIN SELECTION */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Cryptocurrencies
                </label>
                <Tooltip
                  content={
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-medium text-blue-400 mb-2">Cryptocurrency Selection</h5>
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium text-blue-400 mb-1">How to Choose</p>
                            <ul className="space-y-1 text-gray-300">
                              <li className="flex items-center gap-2">
                                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                Start with 3-5 major cryptocurrencies
                              </li>
                              <li className="flex items-center gap-2">
                                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                Choose coins with good liquidity and volume
                              </li>
                              <li className="flex items-center gap-2">
                                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                Diversify across different market sectors
                              </li>
                            </ul>
                          </div>
                          <div>
                            <p className="font-medium text-blue-400 mb-1">Popular Choices</p>
                            <ul className="space-y-1 text-gray-300">
                              <li className="flex items-center gap-2">
                                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                BTC, ETH: Large cap, stable
                              </li>
                              <li className="flex items-center gap-2">
                                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                BNB, ADA, SOL: Mid cap, growth potential
                              </li>
                              <li className="flex items-center gap-2">
                                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                MATIC, DOT, LINK: Utility tokens
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
                        <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="text-blue-300 text-xs">
                          Your bot will trade all selected cryptocurrencies. More coins = more opportunities but also more complexity.
                        </p>
                      </div>
                    </div>
                  }
                  position="right"
                >
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedCoins.length} selected
                </span>
                {pairsError && (
                  <button
                    onClick={() => {
                      console.log(`🔧 [SUB-BLOKAS 3.11.3.1 CoinsSection.tsx] Refetch mygtukas paspaustas`);
                      refetch();
                    }}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    title="Retry loading coins"
                  >
                    <RefreshCw className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            {/* SUB-BLOKAS 3.11.4 CoinsSection.tsx: SEARCH */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search cryptocurrencies..."
                value={searchTerm}
                onChange={(e) => {
                  console.log(`🔧 [SUB-BLOKAS 3.11.4.1 CoinsSection.tsx] Paieškos laukas keičiasi`, { value: e.target.value });
                  setSearchTerm(e.target.value);
                }}
                className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              {searchTerm && (
                <button
                  onClick={() => {
                    console.log(`🔧 [SUB-BLOKAS 3.11.4.2 CoinsSection.tsx] Paieškos išvalymo mygtukas paspaustas`);
                    setSearchTerm('');
                  }}
                  className="absolute right-3 top-2.5 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>

            {/* SUB-BLOKAS 3.11.5 CoinsSection.tsx: LOADING/ERROR STATES */}
            {pairsLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading cryptocurrencies...</span>
              </div>
            )}

            {pairsError && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800/30">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-400 mb-1">
                      Failed to Load Cryptocurrencies
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      {pairsError}
                    </p>
                    <button
                      onClick={() => {
                        console.log(`🔧 [SUB-BLOKAS 3.11.5.1 CoinsSection.tsx] Klaidos atnaujinimo mygtukas paspaustas`);
                        refetch();
                      }}
                      className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 font-medium"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* SUB-BLOKAS 3.11.6 CoinsSection.tsx: COINS GRID */}
            {!pairsLoading && !pairsError && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 max-h-96 overflow-y-auto">
                {filteredCoins.map((coin) => {
                  const isSelected = Array.isArray(selectedCoins) ? selectedCoins.includes(coin) : false;
                  const price = prices[coin];

                  return (
                    <button
                      key={coin}
                      onClick={() => {
                        console.log(`🔧 [SUB-BLOKAS 3.11.6.1 CoinsSection.tsx] Coin pasirinktas/nepasirinktas`, { coin, isSelected });
                        handleToggleCoin(coin);
                      }}
                      className={cn(
                        "p-3 rounded-lg border text-left transition-all",
                        isSelected
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn(
                          "font-medium",
                          isSelected
                            ? "text-blue-600 dark:text-blue-400"
                            : "text-gray-900 dark:text-white"
                        )}>
                          {coin}
                        </span>
                        {isSelected && (
                          <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                        )}
                      </div>

                      {price && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatPrice(price)}
                        </div>
                      )}

                      {pricesLoading && (
                        <div className="text-xs text-gray-400">Loading...</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {filteredCoins.length === 0 && !pairsLoading && !pairsError && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No cryptocurrencies found matching "{searchTerm}"</p>
              </div>
            )}
          </div>

          {/* SUB-BLOKAS 3.11.7 CoinsSection.tsx: SELECTED COINS SUMMARY */}
          {selectedCoins.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800/30">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-1">
                    Selected Cryptocurrencies ({selectedCoins.length})
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedCoins.map((coin) => (
                      <span
                        key={coin}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-800/30 text-blue-800 dark:text-blue-300 text-xs font-medium group"
                      >
                        {coin}/{quoteCurrency}
                        {prices[coin] && (
                          <span className="text-blue-600 dark:text-blue-400">
                            {formatPrice(prices[coin])}
                          </span>
                        )}
                        <button
                          onClick={() => {
                            console.log(`🔧 [SUB-BLOKAS 3.11.7.1 CoinsSection.tsx] Coin pašalinimo mygtukas paspaustas`, { coin });
                            handleToggleCoin(coin);
                          }}
                          className="ml-1 p-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-700/50 transition-colors"
                          title={`Remove ${coin}`}
                        >
                          <X className="h-3 w-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Your bot will trade these pairs: {selectedCoins.map(coin => `${coin}/${quoteCurrency}`).join(', ')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
console.log(`📦 [BLOKAS 3 CoinsSection.tsx] Komponentas CoinsSection apibrėžimas užbaigtas`);